import { FC, useContext, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useAdminFinesApi } from "../../http/fines/api";
import { FineNoCarDependency, getFineStateColor, getFineStatuses } from "../../http/fines/types";
import { NestedPick, PaginationQueryWithFields } from "../../types";
import { formatDateNumeric } from "../../types/utils";
import SvgAdd from "../icons/Add";
import SvgTaskAlt from "../icons/TaskAlt";
import SvgWarning from "../icons/Warning";
import Button from "../ui/Button";
import CustomLink from "../ui/CustomLink";
import Header from "../ui/Header";
import { Action, getDeleteAction, getEditAction } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import Panel from "../ui/Panel";
import { State } from "../ui/State";
import Table from "../ui/Table";
import DateColumnFilter from "../ui/columnFilters/DateColumnFilter";
import TextColumnFilter from "../ui/columnFilters/TextColumnFilter";
import DeleteFineDialog from "./DeleteFineDialog";
import NotifyFineDialog from "./NotifyFineDialog";

const FINE_FIELDS = [
  "id",
  "createdAt",
  "car.id",
  "car.vehicleIdentificationNumber",
  "car.currentPlateNumber",
  "car.lastPlateNumber",
  "verbalNumber",
  "status",
  "violationDate",
  "communicationDate",
  "firstNotificationDate",
  "versionNumber",
  "driver",
  "subsidiary",
  "accountant",
  "hrReferent",
] as const;
type FineField = (typeof FINE_FIELDS)[number];
type FineSearch = NestedPick<FineNoCarDependency, FineField>;

type FinesProps = {
  onUpdate?: () => void;
  redirectUrl?: string;
};

const Fines: FC<FinesProps> = ({ onUpdate, redirectUrl }) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const finesApi = useAdminFinesApi();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ isSearchable: true, prefix: "f" });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const { id: carId } = useParams<{ id: string }>();

  const [itemToDelete, setItemToDelete] = useState<FineSearch>();
  const [fineToNotify, setFineToNotify] = useState<FineSearch>();
  const [fineToArchive, setFineToArchive] = useState<FineSearch>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const query: PaginationQueryWithFields<FineSearch> = {
    ...filters.state,
    q: debouncedValue,
    fields: FINE_FIELDS,
  };

  const result = finesApi.useAdminFines(query, aborter.current.signal, carId);
  const [data] = useDataInitializer(result.data, result.error);

  const filterOptions = {
    get: finesApi.getFieldFilters,
    update: filters.setFilters,
    getSelected: filters.getFilters,
  };

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const params = new URLSearchParams();
  carId && params.set("car_id", carId);
  redirectUrl && params.set("redirect_uri", redirectUrl);

  const navToEdit = (item: FineSearch) => {
    navigate(`/admin/fines/${item.id}?${params.toString()}`);
  };

  const refresh = async () => {
    await result.mutate();
    onUpdate?.();
  };

  const archiveFine = async () => {
    if (!fineToArchive) {
      return;
    }

    setIsSubmitting(true);
    try {
      await finesApi.archiveFine(fineToArchive.id);
      dispatch(showSuccessNotification(i18n.t));
      setFineToArchive(undefined);
      await refresh();
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRowActions = (item: FineSearch): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.fines.update) {
      actions.push(getEditAction(i18n, () => navToEdit(item)));
      actions.push({
        title: i18n.t.fines.notifyDriver,
        onClick: () => setFineToNotify(item),
        icon: SvgWarning,
        color: "blue",
        isDisabled: item.status === "archived",
      });
      actions.push({
        title: i18n.t.fines.archive,
        onClick: () => setFineToArchive(item),
        icon: SvgTaskAlt,
        isDisabled: item.status === "archived",
      });
    }

    if (userSettings.permissions.fines.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), !!actions.length));
    }

    return actions;
  };

  const statuses = getFineStatuses(i18n.t);

  const content = (
    <>
      <Table<FineSearch>
        filters={filters}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        buttons={{
          main: !!userSettings.permissions.fines.insert && (
            <Button
              buttonProps={{
                onClick: () => navigate(`/admin/fines/new?${params.toString()}`),
              }}
              glyph={SvgAdd}
            />
          ),
        }}
        onRowClick={(item, event) => {
          event.preventDefault();
          navToEdit(item);
        }}
        head={
          <>
            <Table.Th title={i18n.t.fines.singular} fieldName="fine.name" />
            {!carId && (
              <>
                <Table.Th
                  title={i18n.t.plateNumbers.singular}
                  fieldName="fine.car.plateNumber"
                  filters={filterOptions}
                  filter={TextColumnFilter}
                  order={orderOptions}
                />
                <Table.Th
                  title={i18n.t.common.carVin}
                  fieldName="fine.car.vin"
                  filters={filterOptions}
                  filter={TextColumnFilter}
                  order={orderOptions}
                />
              </>
            )}
            <Table.Th
              title={i18n.t.drivers.singular}
              fieldName="fine.driver.fullName"
              order={orderOptions}
              filters={filterOptions}
              filter={TextColumnFilter}
            />
            {!carId && (
              <Table.Th
                title={i18n.t.subsidiaries.singular}
                fieldName="fine.car.subsidiary.name"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />
            )}
            <Table.Th
              title={i18n.t.common.state}
              fieldName="fine.status"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
              formatter={(value) => statuses.find((x) => x.value == value)?.label}
            />
            <Table.Th
              title={i18n.t.fines.communicationDate}
              fieldName="fine.communicationDate"
              order={orderOptions}
              filters={filterOptions}
              filter={DateColumnFilter}
              formatter={(value) =>
                typeof value === "string" && value ? formatDateNumeric(value, i18n.language) : undefined
              }
            />
            <Table.Th
              title={i18n.t.fines.firstNotificationDate}
              fieldName="fine.firstNotificationDate"
              order={orderOptions}
              filters={filterOptions}
              filter={DateColumnFilter}
              formatter={(value) =>
                typeof value === "string" && value ? formatDateNumeric(value, i18n.language) : undefined
              }
            />
            <th></th>
          </>
        }
        renderRow={(item) => (
          <>
            {!carId && (
              <>
                <td>
                  {item.verbalNumber} - {formatDateNumeric(item.violationDate, i18n.language)}
                </td>
                <td>
                  {userSettings.permissions.cars.read ? (
                    <CustomLink to={`/admin/cars/${item.car.id}`}>
                      {item.car.currentPlateNumber || item.car.lastPlateNumber}
                    </CustomLink>
                  ) : (
                    <>{item.car.currentPlateNumber || item.car.lastPlateNumber}</>
                  )}
                </td>
                <td>
                  {userSettings.permissions.cars.read ? (
                    <CustomLink to={`/admin/cars/${item.car.id}`}>{item.car.vehicleIdentificationNumber}</CustomLink>
                  ) : (
                    item.car.vehicleIdentificationNumber
                  )}
                </td>
              </>
            )}
            <td>
              {!!userSettings.permissions.users.read ? (
                <CustomLink to={`/admin/users/${item.driver?.id}`}>{item.driver?.fullName}</CustomLink>
              ) : (
                item.driver?.fullName
              )}
            </td>
            {!carId && (
              <td>
                {item.subsidiary && (
                  <CustomLink to={`/admin/subsidiaries/${item.subsidiary.id}`}>{item.subsidiary.name}</CustomLink>
                )}
              </td>
            )}
            <td>
              <State
                text={statuses.find((x) => x.value == item.status)?.label ?? ""}
                color={getFineStateColor(item.status)}
              />
            </td>
            <td>{item.communicationDate ? formatDateNumeric(item.communicationDate, i18n.language) : ""}</td>
            <td>{item.firstNotificationDate ? formatDateNumeric(item.firstNotificationDate, i18n.language) : ""}</td>
          </>
        )}
        rowActions={getRowActions}
        error={result.error}
        items={data?.items}
        total={data?.total}
      />

      {!!itemToDelete && (
        <DeleteFineDialog
          fineId={itemToDelete.id}
          carId={itemToDelete.car.id}
          onClose={(success) => {
            setItemToDelete(undefined);
            success && onUpdate?.();
          }}
        />
      )}

      {!!fineToNotify && (
        <NotifyFineDialog
          fine={fineToNotify}
          onClose={() => setFineToNotify(undefined)}
          onSent={async () => {
            setFineToNotify(undefined);
            await refresh();
          }}
        />
      )}

      {!!fineToArchive && (
        <ModalDialog
          isSubmitting={isSubmitting}
          onClose={() => setFineToArchive(undefined)}
          title={i18n.t.fines.archive}
          actions={[
            {
              title: i18n.t.common.cancel,
              onClick: () => setFineToArchive(undefined),
              type: "secondary",
            },
            {
              title: i18n.t.common.continue,
              onClick: archiveFine,
            },
          ]}
        >
          <div>{i18n.t.fines.questions.archive}</div>
        </ModalDialog>
      )}
    </>
  );

  return carId ? (
    content
  ) : (
    <>
      <Header title={i18n.t.fines.plural} />
      <Panel>{content}</Panel>
    </>
  );
};

export default Fines;
